import { useCallback, useEffect, useRef, useState, type MutableRefObject, type RefObject } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Iphone17Frame } from "./Iphone17Frame";
import { UseCasePhoneScreen } from "./UseCasePhoneScreen";
import { UseCasePhoneWithBadges } from "./useCaseFloatingBadges";
import { useCaseScenarios, type UseCaseScenario } from "./useCaseScenarios";

function UseCaseSectionIntro({ isInView }: { isInView: boolean }) {
  return (
    <div
      className={cn(
        "text-left transition-all duration-700",
        isInView ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
      )}
    >
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary">Casos de uso</p>
      <h2 className="text-balance text-2xl font-bold tracking-tight text-foreground sm:text-3xl xl:text-4xl">
        Feito para o seu <span className="text-primary">dia a dia</span>
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">
        A mesma plataforma em vendas, suporte, saúde, educação e muito mais. Do primeiro contato ao
        pós-venda.
      </p>
      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
        Veja <span className="font-medium text-foreground">conversas de exemplo</span> em cada setor.{" "}
        <span className="lg:hidden">Deslize os chips abaixo ou passe o dedo no telefone para ver todos.</span>
        <span className="hidden lg:inline">
          Escolha um cenário na lista e a rotação pausa para você ler com calma.
        </span>
      </p>
    </div>
  );
}

const ROTATE_MS = 9500;
const TRANSITION_MS = 600;

type UseCasesPhoneCarouselProps = {
  isInView: boolean;
};

function clearIntervalRef(ref: MutableRefObject<ReturnType<typeof setInterval> | null>) {
  if (ref.current) clearInterval(ref.current);
  ref.current = null;
}

export function UseCasesPhoneCarousel({ isInView }: UseCasesPhoneCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [autoPlayPaused, setAutoPlayPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeNavRef = useRef<HTMLButtonElement>(null);
  const mobileNavRef = useRef<HTMLButtonElement>(null);
  const scenarioNavRef = useRef<HTMLElement>(null);
  const touchStartX = useRef(0);
  const total = useCaseScenarios.length;
  const active = useCaseScenarios[activeIndex];

  const goTo = useCallback(
    (index: number) => {
      setActiveIndex(((index % total) + total) % total);
      setProgress(0);
    },
    [total],
  );

  const pauseAutoPlay = useCallback(() => {
    setAutoPlayPaused(true);
    clearIntervalRef(timerRef);
    clearIntervalRef(progressRef);
  }, []);

  const resumeAutoPlay = useCallback(() => {
    setAutoPlayPaused(false);
    setProgress(0);
  }, []);

  useEffect(() => {
    if (!isInView) {
      clearIntervalRef(timerRef);
      clearIntervalRef(progressRef);
      return;
    }
    if (autoPlayPaused) {
      clearIntervalRef(timerRef);
      return;
    }

    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
      setProgress(0);
    }, ROTATE_MS);

    return () => clearIntervalRef(timerRef);
  }, [isInView, autoPlayPaused, total]);

  useEffect(() => {
    if (!isInView || autoPlayPaused) {
      clearIntervalRef(progressRef);
      return;
    }

    const step = 50;
    progressRef.current = setInterval(() => {
      setProgress((p) => (p >= 100 ? 100 : p + (step / ROTATE_MS) * 100));
    }, step);

    return () => clearIntervalRef(progressRef);
  }, [isInView, autoPlayPaused, activeIndex]);

  useEffect(() => {
    if (!isInView) setAutoPlayPaused(false);
  }, [isInView]);

  useEffect(() => {
    if (!isInView) return;
    const nav = scenarioNavRef.current;
    const btn = activeNavRef.current;
    if (!nav || !btn) return;

    const target = btn.offsetTop - nav.clientHeight / 2 + btn.clientHeight / 2;
    nav.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
  }, [activeIndex, isInView]);

  useEffect(() => {
    if (!isInView) return;
    const nav = mobileNavRef.current;
    if (!nav) return;
    nav.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeIndex, isInView]);

  const handleManualSelect = (index: number) => {
    goTo(index);
    pauseAutoPlay();
  };

  const goPrev = () => handleManualSelect(activeIndex - 1);
  const goNext = () => handleManualSelect(activeIndex + 1);

  const handlePhoneTouchStart = (clientX: number) => {
    touchStartX.current = clientX;
  };

  const handlePhoneTouchEnd = (clientX: number) => {
    const delta = clientX - touchStartX.current;
    if (Math.abs(delta) < 48) return;
    if (delta < 0) goNext();
    else goPrev();
  };

  return (
    <div className="use-case-carousel-layout grid items-start gap-8 lg:grid-cols-[minmax(0,400px)_1fr] lg:grid-rows-[auto_1fr] lg:gap-10 xl:grid-cols-[minmax(0,420px)_1fr] xl:gap-12">
      <div className="order-1 lg:col-start-1 lg:row-start-1">
        <UseCaseSectionIntro isInView={isInView} />
      </div>

      <div className="use-case-panel order-3 flex min-h-0 min-w-0 flex-col lg:col-start-1 lg:row-start-2">
        <UseCasePanelCaption active={active} activeIndex={activeIndex} total={total} />
        <UseCaseProgressBar progress={progress} paused={autoPlayPaused} onResume={resumeAutoPlay} />
        <UseCaseScenarioNav
          activeIndex={activeIndex}
          activeNavRef={activeNavRef}
          scenarioNavRef={scenarioNavRef}
          onSelect={handleManualSelect}
        />
        <UseCaseMobileNav
          activeIndex={activeIndex}
          total={total}
          mobileNavRef={mobileNavRef}
          onSelect={handleManualSelect}
          onPrev={goPrev}
          onNext={goNext}
        />
      </div>

      <div className="use-case-phone-col order-2 lg:col-start-2 lg:row-span-2 lg:row-start-1">
        <UseCasePhoneWithBadges scenarioId={active.id}>
          <div
            className="use-case-phone-shell relative touch-pan-y"
            onTouchStart={(e) => handlePhoneTouchStart(e.touches[0]?.clientX ?? 0)}
            onTouchEnd={(e) => handlePhoneTouchEnd(e.changedTouches[0]?.clientX ?? 0)}
          >
            <Iphone17Frame fill className="use-case-carousel-phone use-case-carousel-phone--active">
              <UseCasePhoneSlides activeIndex={activeIndex} />
            </Iphone17Frame>
          </div>
        </UseCasePhoneWithBadges>
      </div>
    </div>
  );
}

function UseCasePanelCaption({
  active,
  activeIndex,
  total,
}: {
  active: UseCaseScenario;
  activeIndex: number;
  total: number;
}) {
  return (
    <div key={active.id} className="use-case-caption-enter shrink-0">
      <div className={cn("mb-3 flex h-12 w-12 items-center justify-center rounded-xl shadow-md", active.accent)}>
        <active.icon className="h-6 w-6 text-white" aria-hidden />
      </div>
      <p className="text-xs font-semibold uppercase tracking-wider text-primary">
        {String(activeIndex + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </p>
      <h3 className="mt-1.5 text-xl font-bold leading-tight text-foreground xl:text-2xl">{active.title}</h3>
      <p className="mt-1 text-sm font-medium text-primary">{active.subtitle}</p>
      <p className="mt-3 line-clamp-3 min-h-[4.25rem] text-sm leading-relaxed text-muted-foreground">{active.description}</p>
      <div className="mt-3 min-h-[1.25rem]">
        {active.link ? (
          <Link to={active.link} className="inline-flex items-center text-sm font-semibold text-primary hover:underline">
            Saiba mais →
          </Link>
        ) : null}
      </div>
    </div>
  );
}

function UseCaseProgressBar({
  progress,
  paused,
  onResume,
}: {
  progress: number;
  paused: boolean;
  onResume: () => void;
}) {
  return (
    <div className="mt-5 shrink-0">
      <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{paused ? "Rotação pausada" : "Próximo cenário"}</span>
        {paused ? (
          <button
            type="button"
            onClick={onResume}
            className="shrink-0 font-medium text-primary hover:underline"
          >
            Retomar rotação
          </button>
        ) : (
          <span>{Math.ceil((ROTATE_MS / 1000) * (1 - progress / 100))}s</span>
        )}
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            "h-full rounded-full bg-primary transition-[width] duration-100 ease-linear",
            paused && "w-0 opacity-0",
          )}
          style={{ width: paused ? "0%" : `${progress}%` }}
        />
      </div>
    </div>
  );
}

function scenarioChipClass(isActive: boolean) {
  return cn(
    "flex shrink-0 items-center gap-2 rounded-lg border text-left transition-colors duration-200",
    isActive
      ? "border-primary/35 bg-primary/10 text-foreground"
      : "border-border/40 bg-background text-muted-foreground hover:border-border hover:bg-muted/50",
  );
}

function UseCaseMobileNav({
  activeIndex,
  total,
  mobileNavRef,
  onSelect,
  onPrev,
  onNext,
}: {
  activeIndex: number;
  total: number;
  mobileNavRef: RefObject<HTMLButtonElement | null>;
  onSelect: (index: number) => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="mt-4 shrink-0 lg:hidden">
      <nav
        className="use-case-mobile-nav -mx-1 flex gap-2 overflow-x-auto px-1 pb-2 scroll-smooth snap-x snap-mandatory"
        aria-label="Cenários de uso"
      >
        {useCaseScenarios.map((scenario, index) => {
          const Icon = scenario.icon;
          const isActive = index === activeIndex;
          return (
            <button
              key={scenario.id}
              ref={isActive ? mobileNavRef : undefined}
              type="button"
              onClick={() => onSelect(index)}
              className={cn(scenarioChipClass(isActive), "h-11 min-w-[9.5rem] snap-start px-2.5")}
            >
              <div
                className={cn(
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
                  scenario.accent,
                  !isActive && "opacity-80",
                )}
              >
                <Icon className="h-3.5 w-3.5 text-white" aria-hidden />
              </div>
              <span className="min-w-0 truncate text-xs font-medium leading-none">{scenario.title}</span>
            </button>
          );
        })}
      </nav>

      <div className="mt-3 flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 flex-1 gap-1 rounded-lg"
          onClick={onPrev}
          aria-label="Cenário anterior"
        >
          <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
          Anterior
        </Button>
        <span className="shrink-0 px-1 text-xs font-semibold tabular-nums text-muted-foreground">
          {String(activeIndex + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 flex-1 gap-1 rounded-lg"
          onClick={onNext}
          aria-label="Próximo cenário"
        >
          Próximo
          <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

function UseCaseScenarioNav({
  activeIndex,
  activeNavRef,
  scenarioNavRef,
  onSelect,
}: {
  activeIndex: number;
  activeNavRef: RefObject<HTMLButtonElement | null>;
  scenarioNavRef: RefObject<HTMLElement | null>;
  onSelect: (index: number) => void;
}) {
  return (
    <nav
      ref={scenarioNavRef}
      className="use-case-scenario-nav mt-4 hidden min-h-0 flex-1 grid-cols-2 gap-1.5 overflow-y-auto pr-1 lg:grid"
      aria-label="Todos os cenários"
    >
      {useCaseScenarios.map((scenario, index) => {
        const Icon = scenario.icon;
        const isActive = index === activeIndex;
        return (
          <button
            key={scenario.id}
            ref={isActive ? activeNavRef : undefined}
            type="button"
            onClick={() => onSelect(index)}
            className={cn(scenarioChipClass(isActive), "h-10 px-2.5")}
          >
            <div className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-md", scenario.accent, !isActive && "opacity-80")}>
              <Icon className="h-3.5 w-3.5 text-white" aria-hidden />
            </div>
            <span className="min-w-0 truncate text-xs font-medium leading-none">{scenario.title}</span>
          </button>
        );
      })}
    </nav>
  );
}

function UseCasePhoneSlides({ activeIndex }: { activeIndex: number }) {
  return (
    <div className="relative h-full w-full">
      {useCaseScenarios.map((scenario, index) => {
        const isActive = index === activeIndex;
        const isBefore = index < activeIndex;
        return (
          <div
            key={scenario.id}
            className={cn(
              "absolute inset-0 transition-[transform,opacity] ease-[cubic-bezier(0.32,0.72,0,1)]",
              isActive && "z-10 translate-x-0 opacity-100",
              !isActive && isBefore && "z-0 -translate-x-full opacity-0",
              !isActive && !isBefore && "z-0 translate-x-full opacity-0",
            )}
            style={{ transitionDuration: `${TRANSITION_MS}ms` }}
            aria-hidden={!isActive}
          >
            <UseCasePhoneScreen scenario={scenario} />
          </div>
        );
      })}
    </div>
  );
}

